import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { UIEvent, MouseEvent } from 'react';
import { useDispatch } from 'react-redux';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import apiService from 'app/apiService';
import { useNurturingTestAutomationProgress } from 'app/hooks/useNurturingTestAutomationProgress';
import { notificationAlertActions } from 'app/pages/NotificationAlert/slice';
import TestNurturingApiService, {
  buildUnsoldLeadsCombinedFiltersJson,
} from 'app/pages/TestNurturingPage/apiService/testNurturingApiService';
import type { DealerSegmentSettingsResponse } from 'app/pages/TestNurturingPage/apiService/testNurturingApiService';
import type {
  FilterTrayCategory,
  FilterTrayOption,
  FilterTraySelection,
} from 'app/components/ReusableUIComponents/HighLevelComponents/FilterTrayMAIQ';

import type { TestNurturingAutomationStep } from 'app/pages/TestNurturingPage/utils/testNurturingAutomationLeadUtils';
import {
  getNurturingStageOptionValue,
  getNurturingStagesArrayFromApiResponse,
  getUnsoldLeadRowId,
  getUnsoldLeadSegmentNameRaw,
} from 'app/pages/TestNurturingPage/utils/testNurturingAutomationLeadUtils';
import { isNurturingAutomationHttpSuccess } from 'app/utils/nurturingTestAutomationProgressUtils';

const UNSOLD_PAGE_SIZE = 20;
/** Avoid refetching the full infinite-query stack on focus/mount (global RQ defaults refetch all pages). */
const UNSOLD_LEADS_STALE_TIME_MS = 60_000;

/** Quick Filter tray category id — matches unsold `filters` field `maiq_segment`. */
const UNSOLD_QUICK_FILTER_MAIQ_SEGMENT = 'maiq_segment';

function getDealerSegmentsSettingsBody(
  data: unknown,
): DealerSegmentSettingsResponse | null {
  if (!data) return null;
  const response = data as {
    body?: DealerSegmentSettingsResponse;
    data?: DealerSegmentSettingsResponse;
  };
  return response.body ?? response.data ?? (data as DealerSegmentSettingsResponse);
}

function getUniquePrimarySegmentsFromSettings(data: unknown): string[] {
  const body = getDealerSegmentsSettingsBody(data);
  const settingsDoc = Array.isArray(body)
    ? body[0]?.settings?.[0]
    : body && typeof body === 'object'
      ? body
      : null;
  const segments = settingsDoc?.properties?.segments;

  if (!Array.isArray(segments)) return [];

  return Array.from(
    new Set(
      segments
        .map(segment => segment.primary_segment?.trim())
        .filter((segment): segment is string => Boolean(segment)),
    ),
  );
}

function getUnsoldListBody(res: unknown): {
  data: unknown[];
  totalDocuments: number;
  totalPages: number;
} {
  const root = res as { body?: { data?: unknown[]; meta?: { totalDocuments?: number; totalPages?: number } } };
  const body = root?.body ?? (res as { data?: unknown[]; meta?: { totalDocuments?: number; totalPages?: number } });
  const data = Array.isArray(body?.data) ? body.data : [];
  const totalDocuments =
    typeof body?.meta?.totalDocuments === 'number' ? body.meta.totalDocuments : data.length;
  const totalPagesFromMeta =
    typeof body?.meta?.totalPages === 'number' ? body.meta.totalPages : null;
  const totalPages =
    totalPagesFromMeta != null && totalPagesFromMeta > 0
      ? totalPagesFromMeta
      : Math.max(1, Math.ceil(totalDocuments / UNSOLD_PAGE_SIZE) || 1);

  return { data, totalDocuments, totalPages };
}

export type UseTestNurturingAutomationModalOptions = {
  /** Called after a successful test run (e.g. switch to Nurturing Journeys tab). */
  onTestSuccess?: () => void;
};

export function useTestNurturingAutomationModal(
  dealerId: string,
  canAccess: boolean,
  options: UseTestNurturingAutomationModalOptions = {},
) {
  const { onTestSuccess } = options;
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState<TestNurturingAutomationStep>(1);
  const [primarySegment, setPrimarySegment] = useState('');
  const [unsoldQuerySession, setUnsoldQuerySession] = useState(0);
  const [selectedLeadIds, setSelectedLeadIds] = useState<string[]>([]);
  const [unsoldSearchDraft, setUnsoldSearchDraft] = useState('');
  const [unsoldSearchApplied, setUnsoldSearchApplied] = useState('');
  const [selectedNurturingStageValue, setSelectedNurturingStageValue] =
    useState('');
  const [unsoldQuickFilterOpen, setUnsoldQuickFilterOpen] = useState(false);
  const [unsoldQuickFilterAnchorEl, setUnsoldQuickFilterAnchorEl] =
    useState<HTMLElement | null>(null);
  const [unsoldQuickFilterActiveCategoryId, setUnsoldQuickFilterActiveCategoryId] =
    useState<string | null>(UNSOLD_QUICK_FILTER_MAIQ_SEGMENT);
  const [unsoldQuickFilterSelectedByCategory, setUnsoldQuickFilterSelectedByCategory] =
    useState<FilterTraySelection>({});
  const [startTestingLoading, setStartTestingLoading] = useState(false);
  const [startTestingError, setStartTestingError] = useState<string | null>(null);

  const selectedLeadIdsRef = useRef<string[]>(selectedLeadIds);
  const unsoldPaginationLockRef = useRef(false);

  const nurturingAutomation = useNurturingTestAutomationProgress({
    dealerId,
    canAccess,
    enablePersistence: true,
    onComplete: onTestSuccess,
  });

  useEffect(() => {
    selectedLeadIdsRef.current = selectedLeadIds;
  }, [selectedLeadIds]);

  const {
    data: dealerSegmentSettings,
    isLoading: dealerSegmentSettingsLoading,
    error: dealerSegmentSettingsError,
  } = useQuery({
    queryKey: ['test-nurturing-dealer-segment-settings', dealerId],
    queryFn: () => TestNurturingApiService.getDealerSegmentSettings(dealerId),
    enabled: Boolean(dealerId) && canAccess && open,
  });

  const primarySegmentOptions = useMemo(
    () => getUniquePrimarySegmentsFromSettings(dealerSegmentSettings),
    [dealerSegmentSettings],
  );

  const dealerSegmentSettingsErrorMessage = useMemo(() => {
    if (!dealerSegmentSettingsError) return null;
    if (
      typeof dealerSegmentSettingsError === 'object' &&
      dealerSegmentSettingsError !== null &&
      'message' in dealerSegmentSettingsError
    ) {
      return String(
        (dealerSegmentSettingsError as { message?: unknown }).message,
      );
    }
    return 'Failed to load dealer segment settings';
  }, [dealerSegmentSettingsError]);

  const {
    data: nurturingJourneyStagesResponse,
    isLoading: nurturingJourneyStagesLoading,
    error: nurturingJourneyStagesError,
  } = useQuery({
    queryKey: ['test-nurturing-nurturing-journey-stages'],
    queryFn: () => TestNurturingApiService.getNurturingJourneyStages(),
    enabled: canAccess && open && step === 3,
  });

  const nurturingStageRows = useMemo(
    () => getNurturingStagesArrayFromApiResponse(nurturingJourneyStagesResponse),
    [nurturingJourneyStagesResponse],
  );

  const nurturingJourneyStagesErrorMessage = useMemo(() => {
    if (!nurturingJourneyStagesError) return null;
    if (
      typeof nurturingJourneyStagesError === 'object' &&
      nurturingJourneyStagesError !== null &&
      'message' in nurturingJourneyStagesError
    ) {
      return String(
        (nurturingJourneyStagesError as { message?: unknown }).message,
      );
    }
    return 'Failed to load nurturing journey stages';
  }, [nurturingJourneyStagesError]);

  const unsoldPrimarySegmentQuery = primarySegment.trim();

  const unsoldFiltersJson = useMemo(
    () =>
      buildUnsoldLeadsCombinedFiltersJson({
        primarySegment,
        segmentNames:
          unsoldQuickFilterSelectedByCategory[UNSOLD_QUICK_FILTER_MAIQ_SEGMENT] ??
          [],
        unifiedSearchText: unsoldSearchApplied,
      }),
    [primarySegment, unsoldQuickFilterSelectedByCategory, unsoldSearchApplied],
  );

  const {
    data: unsoldInfiniteData,
    isLoading: unsoldLoading,
    isFetching: unsoldFetching,
    isFetchingNextPage: unsoldIsFetchingNextPage,
    fetchNextPage,
    hasNextPage,
    error: unsoldError,
  } = useInfiniteQuery({
    queryKey: [
      'test-nurturing-unsold-leads',
      dealerId,
      unsoldQuerySession,
      unsoldPrimarySegmentQuery,
      unsoldFiltersJson,
    ],
    queryFn: ({ pageParam }) => {
      const page =
        typeof pageParam === 'number' && pageParam >= 1 ? pageParam : 1;
      return TestNurturingApiService.getUnsoldLeadsPage({
        dealerId,
        page,
        pageSize: UNSOLD_PAGE_SIZE,
        segment: unsoldPrimarySegmentQuery,
        filters: unsoldFiltersJson,
      });
    },
    enabled: Boolean(dealerId) && canAccess && open && step === 2,
    getNextPageParam: (lastPage, allPages) => {
      const { totalPages, data } = getUnsoldListBody(lastPage);
      const loaded = allPages.length;
      if (!data.length || loaded >= totalPages) return undefined;
      return loaded + 1;
    },
    staleTime: UNSOLD_LEADS_STALE_TIME_MS,
    cacheTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });

  useEffect(() => {
    unsoldPaginationLockRef.current = false;
  }, [dealerId, unsoldQuerySession, unsoldPrimarySegmentQuery, unsoldFiltersJson]);

  const unsoldRows = useMemo(() => {
    if (!unsoldInfiniteData?.pages?.length) return [];
    const seen = new Set<string>();
    const out: unknown[] = [];
    for (const pageRes of unsoldInfiniteData.pages) {
      const { data } = getUnsoldListBody(pageRes);
      for (const row of data) {
        const id = getUnsoldLeadRowId(row);
        if (id) {
          if (!seen.has(id)) {
            seen.add(id);
            out.push(row);
          }
        } else {
          out.push(row);
        }
      }
    }
    return out;
  }, [unsoldInfiniteData]);

  const unsoldTotalDocuments = useMemo(() => {
    if (!unsoldInfiniteData?.pages?.[0]) return 0;
    return getUnsoldListBody(unsoldInfiniteData.pages[0]).totalDocuments;
  }, [unsoldInfiniteData]);

  const unsoldHasNextPage = Boolean(hasNextPage);

  const unsoldErrorMessage = useMemo(() => {
    if (!unsoldError) return null;
    if (
      typeof unsoldError === 'object' &&
      unsoldError !== null &&
      'message' in unsoldError
    ) {
      return String((unsoldError as { message?: unknown }).message);
    }
    return 'Failed to load unsold leads';
  }, [unsoldError]);

  const resetTransientState = useCallback(() => {
    setStep(1);
    setPrimarySegment('');
    setSelectedLeadIds([]);
    setUnsoldSearchDraft('');
    setUnsoldSearchApplied('');
    setSelectedNurturingStageValue('');
    setUnsoldQuickFilterSelectedByCategory({});
    setUnsoldQuickFilterOpen(false);
    setUnsoldQuickFilterAnchorEl(null);
    setUnsoldQuickFilterActiveCategoryId(UNSOLD_QUICK_FILTER_MAIQ_SEGMENT);
    setStartTestingLoading(false);
    setStartTestingError(null);
  }, []);

  const prevDealerIdRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (prevDealerIdRef.current === undefined) {
      prevDealerIdRef.current = dealerId;
      return;
    }
    if (prevDealerIdRef.current !== dealerId) {
      prevDealerIdRef.current = dealerId;
      setOpen(false);
      resetTransientState();
      nurturingAutomation.resetSession();
    }
  }, [dealerId, nurturingAutomation.resetSession, resetTransientState]);

  const openModal = useCallback(() => {
    resetTransientState();
    setOpen(true);
  }, [resetTransientState]);

  const closeModal = useCallback(() => {
    setOpen(false);
    resetTransientState();
  }, [resetTransientState]);

  useEffect(() => {
    if (
      primarySegment &&
      !primarySegmentOptions.includes(primarySegment)
    ) {
      setPrimarySegment('');
    }
  }, [primarySegment, primarySegmentOptions]);

  useEffect(() => {
    if (!nurturingStageRows.length) {
      if (selectedNurturingStageValue) setSelectedNurturingStageValue('');
      return;
    }
    const stillValid = nurturingStageRows.some(
      (row, index) =>
        getNurturingStageOptionValue(row, index) === selectedNurturingStageValue,
    );
    if (!stillValid && selectedNurturingStageValue) {
      setSelectedNurturingStageValue('');
    }
  }, [nurturingStageRows, selectedNurturingStageValue]);

  const goToAutomationStep2 = useCallback(() => {
    setUnsoldSearchDraft('');
    setUnsoldSearchApplied('');
    setUnsoldQuickFilterSelectedByCategory({});
    setUnsoldQuickFilterOpen(false);
    setUnsoldQuickFilterAnchorEl(null);
    setUnsoldQuickFilterActiveCategoryId(UNSOLD_QUICK_FILTER_MAIQ_SEGMENT);
    setUnsoldQuerySession(s => s + 1);
    setSelectedLeadIds([]);
    setStep(2);
  }, []);

  const applyUnsoldSearch = useCallback(() => {
    setUnsoldSearchApplied(unsoldSearchDraft.trim());
    setSelectedLeadIds([]);
  }, [unsoldSearchDraft]);

  const goToAutomationStep1 = useCallback(() => {
    setStep(1);
  }, []);

  const toggleLeadSelection = useCallback((leadId: string) => {
    if (!leadId) return;
    setSelectedLeadIds(prev =>
      prev.includes(leadId) ? prev.filter(id => id !== leadId) : [...prev, leadId],
    );
  }, []);

  const isLeadSelected = useCallback(
    (leadId: string) => selectedLeadIds.includes(leadId),
    [selectedLeadIds],
  );

  const goToAutomationStep3 = useCallback(() => {
    setStartTestingError(null);
    setStep(3);
  }, []);

  const goBackToAutomationStep2 = useCallback(() => {
    setStartTestingError(null);
    setStep(2);
  }, []);

  const finishAutomationFlow = useCallback(async () => {
    const stage = selectedNurturingStageValue.trim();
    if (!dealerId.trim() || !stage || selectedLeadIds.length === 0) {
      return;
    }
    setStartTestingError(null);
    setStartTestingLoading(true);
    try {
      const response = await TestNurturingApiService.postNurturingAutomationTest({
        dealerId,
        leadIds: selectedLeadIds,
        nurturingStage: stage,
        primarySegment,
      });

      if (!isNurturingAutomationHttpSuccess(response.statusCode)) {
        const msg = response.message ? String(response.message) : '';
        if (msg) {
          setStartTestingError(msg);
          dispatch(
            notificationAlertActions.addNotification({
              open: true,
              severity: 'error',
              message: msg,
            }),
          );
        }
        return;
      }

      const responseBody =
        response && typeof response.body === 'object' && response.body !== null
          ? (response.body as Record<string, unknown>)
          : null;
      const responseJobId = responseBody
        ? String(responseBody.job_id ?? responseBody.jobId ?? '').trim()
        : '';
      const responseLeadCountRaw =
        responseBody?.lead_count ?? responseBody?.total_leads;
      const responseLeadCount =
        typeof responseLeadCountRaw === 'number' && Number.isFinite(responseLeadCountRaw)
          ? Math.max(1, responseLeadCountRaw)
          : Math.max(1, selectedLeadIds.length);

      nurturingAutomation.startSession({
        jobIds: responseJobId ? [responseJobId] : [],
        primaryJobId: responseJobId,
        progressTotal: responseLeadCount,
      });

      void apiService.invalidateCacheByPrefix('api/v2/leads', 'GET');

      const successMessage = response.message ? String(response.message) : '';
      if (successMessage) {
        dispatch(
          notificationAlertActions.addNotification({
            open: true,
            severity: 'success',
            message: successMessage,
          }),
        );
      }

      closeModal();
    } catch (err: unknown) {
      const msg =
        err && typeof err === 'object' && 'message' in err
          ? String((err as { message: unknown }).message)
          : '';
      if (msg) {
        setStartTestingError(msg);
        dispatch(
          notificationAlertActions.addNotification({
            open: true,
            severity: 'error',
            message: msg,
          }),
        );
      }
    } finally {
      setStartTestingLoading(false);
    }
  }, [
    closeModal,
    dealerId,
    dispatch,
    nurturingAutomation.startSession,
    primarySegment,
    selectedLeadIds,
    selectedNurturingStageValue,
  ]);

  const handleUnsoldListScroll = useCallback(
    (e: UIEvent<HTMLDivElement>) => {
      if (
        !hasNextPage ||
        unsoldIsFetchingNextPage ||
        unsoldLoading ||
        unsoldFetching ||
        unsoldPaginationLockRef.current
      ) {
        return;
      }

      const el = e.currentTarget;
      const thresholdPx = 140;
      const distanceToBottom =
        el.scrollHeight - el.scrollTop - el.clientHeight;

      if (distanceToBottom >= thresholdPx) return;

      unsoldPaginationLockRef.current = true;
      void fetchNextPage().finally(() => {
        unsoldPaginationLockRef.current = false;
      });
    },
    [
      hasNextPage,
      unsoldIsFetchingNextPage,
      unsoldLoading,
      unsoldFetching,
      fetchNextPage,
    ],
  );

  const onUnsoldQuickFilterSelectionChange = useCallback(
    (categoryId: string, selectedValues: string[]) => {
      if (categoryId !== UNSOLD_QUICK_FILTER_MAIQ_SEGMENT) return;
      let next = selectedValues;
      if (primarySegment.trim() && next.length > 1) {
        const last = next[next.length - 1];
        next = last ? [last] : [];
      }
      setUnsoldQuickFilterSelectedByCategory(prev => ({
        ...prev,
        [categoryId]: next,
      }));
      setSelectedLeadIds([]);
    },
    [primarySegment],
  );

  const onUnsoldQuickFilterClearAll = useCallback(() => {
    setUnsoldQuickFilterSelectedByCategory({});
    setSelectedLeadIds([]);
  }, []);

  const onUnsoldQuickFilterClearActiveCategory = useCallback(() => {
    if (!unsoldQuickFilterActiveCategoryId) return;
    setUnsoldQuickFilterSelectedByCategory(prev => ({
      ...prev,
      [unsoldQuickFilterActiveCategoryId]: [],
    }));
    setSelectedLeadIds([]);
  }, [unsoldQuickFilterActiveCategoryId]);

  const onUnsoldQuickFilterClearAllInPanel = useCallback(() => {
    setUnsoldQuickFilterSelectedByCategory({});
    setSelectedLeadIds([]);
  }, []);

  const openUnsoldQuickFilter = useCallback((event: MouseEvent<HTMLElement>) => {
    setUnsoldQuickFilterAnchorEl(event.currentTarget);
    setUnsoldQuickFilterOpen(true);
    setUnsoldQuickFilterActiveCategoryId(UNSOLD_QUICK_FILTER_MAIQ_SEGMENT);
  }, []);

  const closeUnsoldQuickFilter = useCallback(() => {
    setUnsoldQuickFilterOpen(false);
    setUnsoldQuickFilterAnchorEl(null);
  }, []);

  const clearUnsoldQuickFiltersAndSearch = useCallback(() => {
    setUnsoldQuickFilterSelectedByCategory({});
    setUnsoldSearchDraft('');
    setUnsoldSearchApplied('');
    setSelectedLeadIds([]);
    setUnsoldQuickFilterOpen(false);
    setUnsoldQuickFilterAnchorEl(null);
    setUnsoldQuickFilterActiveCategoryId(UNSOLD_QUICK_FILTER_MAIQ_SEGMENT);
  }, []);

  const unsoldQuickFilterTotalActive = useMemo(
    () =>
      Object.values(unsoldQuickFilterSelectedByCategory).reduce(
        (t, vals) => t + (vals?.length ?? 0),
        0,
      ),
    [unsoldQuickFilterSelectedByCategory],
  );

  const unsoldQuickFilterCategories: FilterTrayCategory[] = useMemo(
    () => [
      {
        id: UNSOLD_QUICK_FILTER_MAIQ_SEGMENT,
        label: 'Segment name',
        selectedCount:
          (unsoldQuickFilterSelectedByCategory[UNSOLD_QUICK_FILTER_MAIQ_SEGMENT] ?? [])
            .length,
      },
    ],
    [unsoldQuickFilterSelectedByCategory],
  );

  const unsoldQuickFilterOptionsForActiveCategory: FilterTrayOption[] =
    useMemo(() => {
      if (unsoldQuickFilterActiveCategoryId !== UNSOLD_QUICK_FILTER_MAIQ_SEGMENT) {
        return [];
      }
      const fromRows = new Set<string>();
      for (const row of unsoldRows) {
        const n = getUnsoldLeadSegmentNameRaw(row);
        if (n) fromRows.add(n);
      }
      const selected =
        unsoldQuickFilterSelectedByCategory[UNSOLD_QUICK_FILTER_MAIQ_SEGMENT] ??
        [];
      for (const s of selected) {
        if (s) fromRows.add(s);
      }
      return Array.from(fromRows)
        .sort((a, b) => a.localeCompare(b))
        .map(value => ({ value, label: value }));
    }, [
      unsoldQuickFilterActiveCategoryId,
      unsoldRows,
      unsoldQuickFilterSelectedByCategory,
    ]);

  const isStep1NextDisabled =
    dealerSegmentSettingsLoading || !primarySegment.trim();
  const isStep2NextDisabled = selectedLeadIds.length === 0;
  return {
    testAutomationModalOpen: open,
    openTestAutomationModal: openModal,
    closeTestAutomationModal: closeModal,
    testAutomationStep: step,
    goToAutomationStep2,
    goToAutomationStep1,
    goToAutomationStep3,
    goBackToAutomationStep2,
    finishAutomationFlow,
    startTestingLoading,
    startTestingError,
    automationInProgress: nurturingAutomation.automationInProgress,
    automationProgressPercentage:
      nurturingAutomation.automationProgressPercentage,
    automationProgressCurrent: nurturingAutomation.automationProgressCurrent,
    automationProgressTotal: nurturingAutomation.automationProgressTotal,
    automationStreamingDataForCard:
      nurturingAutomation.automationStreamingDataForCard,
    automationCurrentSectionForCard:
      nurturingAutomation.automationCurrentSectionForCard,
    automationSocketMessages: nurturingAutomation.automationSocketMessages,
    automationCompletionPayload:
      nurturingAutomation.automationCompletionPayload,
    testAutomationPrimarySegment: primarySegment,
    testAutomationPrimarySegmentOptions: primarySegmentOptions,
    dealerSegmentSettingsLoading,
    dealerSegmentSettingsErrorMessage,
    selectTestAutomationPrimarySegment: setPrimarySegment,
    isStep1NextDisabled,
    isStep2NextDisabled,
    unsoldRows,
    unsoldLoading,
    unsoldFetching,
    unsoldIsFetchingNextPage,
    unsoldErrorMessage,
    unsoldTotalDocuments,
    unsoldHasNextPage,
    onUnsoldListScroll: handleUnsoldListScroll,
    unsoldSearchDraft,
    unsoldSearchApplied,
    setUnsoldSearchDraft,
    applyUnsoldSearch,
    selectedLeadIds,
    toggleLeadSelection,
    isLeadSelected,
    nurturingStageRows,
    nurturingJourneyStagesLoading,
    nurturingJourneyStagesErrorMessage,
    selectedNurturingStageValue,
    selectNurturingStageValue: setSelectedNurturingStageValue,
    unsoldQuickFilterOpen,
    unsoldQuickFilterAnchorEl,
    unsoldQuickFilterActiveCategoryId,
    setUnsoldQuickFilterActiveCategoryId,
    unsoldQuickFilterCategories,
    unsoldQuickFilterOptionsForActiveCategory,
    unsoldQuickFilterSelectedByCategory,
    onUnsoldQuickFilterSelectionChange,
    unsoldQuickFilterTotalActive,
    onUnsoldQuickFilterClearAll,
    onUnsoldQuickFilterClearActiveCategory,
    onUnsoldQuickFilterClearAllInPanel,
    openUnsoldQuickFilter,
    closeUnsoldQuickFilter,
    clearUnsoldQuickFiltersAndSearch,
  };
}
